import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    IconButton,
    CircularProgress,
    Tooltip,
    Divider,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PortalLayout from './PortalLayout';
import PortalPageHeader from './PortalPageHeader';
import PortalStatusPill from './PortalStatusPill';
import QuickViewDrawer from '../sub-components/QuickViewDrawer';
import { portalCardSx, statusColor } from './portalStyles';
import PortalInvoiceService from '../../services/PortalInvoiceService';

function formatDate(dateStr) {
    if (!dateStr) return '—';
    try {
        return new Date(dateStr).toLocaleDateString('de-CH', {
            day: '2-digit', month: '2-digit', year: 'numeric',
        });
    } catch {
        return dateStr;
    }
}

function formatAmount(amount) {
    if (amount == null) return '—';
    return new Intl.NumberFormat('de-CH', { style: 'currency', currency: 'CHF' }).format(amount);
}

export default function PortalInvoicesPage() {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [downloadingId, setDownloadingId] = useState(null);   // invoice id or "cn-{id}"

    const [quickView, setQuickView] = useState(null);

    useEffect(() => {
        loadInvoices();
    }, []);

    const loadInvoices = async () => {
        setLoading(true);
        try {
            const data = await PortalInvoiceService.getInvoices();
            setInvoices(data || []);
        } catch (err) {
            console.error('Failed to load invoices', err);
        } finally {
            setLoading(false);
        }
    };

    const triggerDownload = (blob, filename) => {
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(blobUrl);
    };

    const handleDownload = async (invoice) => {
        setDownloadingId(invoice.id);
        try {
            const response = await PortalInvoiceService.downloadPdf(invoice.id);
            const cd = response.headers['content-disposition'] || '';
            const match = cd.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
            const filename = match
                ? decodeURIComponent(match[1].replace(/"/g, ''))
                : `invoice-${invoice.id}.pdf`;
            triggerDownload(new Blob([response.data], { type: 'application/pdf' }), filename);
        } catch (err) {
            console.error('Failed to download invoice PDF', err);
        } finally {
            setDownloadingId(null);
        }
    };

    const handleDownloadCreditNote = async (cn) => {
        const key = `cn-${cn.id}`;
        setDownloadingId(key);
        try {
            const response = await PortalInvoiceService.downloadCreditNotePdf(cn.id);
            triggerDownload(
                new Blob([response.data], { type: 'application/pdf' }),
                `${cn.displayName || `credit-note-${cn.id}`}.pdf`
            );
        } catch (err) {
            console.error('Failed to download credit note PDF', err);
        } finally {
            setDownloadingId(null);
        }
    };

    const issuedCreditNotes = (invoice) =>
        (invoice?.creditNotes || []).filter(cn => cn.status === 'ISSUED' || cn.amount != null);

    const creditedTotal = (invoice) =>
        issuedCreditNotes(invoice).reduce((sum, cn) => sum + (parseFloat(cn.amount) || 0), 0);

    return (
        <PortalLayout>
            <PortalPageHeader title="Invoices" subtitle="Your invoices across all projects" />

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress sx={{ color: '#427898' }} />
                </Box>
            ) : invoices.length === 0 ? (
                <Paper elevation={0} sx={{ ...portalCardSx, p: 6, textAlign: 'center' }}>
                    <Typography variant="body1" sx={{ color: '#94a3b8' }}>No invoices available</Typography>
                </Paper>
            ) : (
                <TableContainer component={Paper} elevation={0} sx={portalCardSx}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Invoice</TableCell>
                                <TableCell>Project</TableCell>
                                <TableCell>Date</TableCell>
                                <TableCell>Due date</TableCell>
                                <TableCell align="right">Amount</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {invoices.map((invoice) => {
                                const credited = creditedTotal(invoice);
                                const hasCreditNotes = credited > 0;
                                const netAmount = (invoice.totalAmount || 0) - credited;

                                return (
                                    <TableRow
                                        key={invoice.id}
                                        hover
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => setQuickView(invoice)}
                                    >
                                        <TableCell sx={{ color: '#1a2332', fontWeight: 500 }}>
                                            {invoice.displayName || `Invoice #${invoice.id}`}
                                        </TableCell>
                                        <TableCell sx={{ color: '#64748b' }}>
                                            {invoice.projectName || '—'}
                                        </TableCell>
                                        <TableCell sx={{ color: '#64748b' }}>
                                            {formatDate(invoice.invoiceDate)}
                                        </TableCell>
                                        <TableCell sx={{ color: '#64748b' }}>
                                            {formatDate(invoice.dueDate)}
                                        </TableCell>
                                        <TableCell align="right">
                                            {hasCreditNotes ? (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25 }}>
                                                    <Typography sx={{ fontSize: 12, color: '#94a3b8', textDecoration: 'line-through' }}>
                                                        {formatAmount(invoice.totalAmount)}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: 12, color: '#e57373' }}>
                                                        − {formatAmount(credited)}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1a2332' }}>
                                                        {formatAmount(netAmount)}
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#1a2332' }}>
                                                    {formatAmount(invoice.totalAmount)}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                                                <PortalStatusPill status={invoice.status} />
                                                {hasCreditNotes && (
                                                    <Chip
                                                        icon={<ReceiptIcon sx={{ fontSize: '12px !important' }} />}
                                                        label="Credit Note"
                                                        size="small"
                                                        sx={{ backgroundColor: '#fff3e0', color: '#e65100', fontWeight: 600, fontSize: 11, borderRadius: '6px' }}
                                                    />
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                                            <Tooltip title="Download PDF">
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDownload(invoice)}
                                                        disabled={downloadingId === invoice.id}
                                                        sx={{ color: '#427898' }}
                                                    >
                                                        {downloadingId === invoice.id
                                                            ? <CircularProgress size={18} sx={{ color: '#427898' }} />
                                                            : <DownloadIcon fontSize="small" />}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Quick-view drawer — PDF preview + facts + credit notes */}
            <QuickViewDrawer
                open={Boolean(quickView)}
                onClose={() => setQuickView(null)}
                title={quickView?.displayName || (quickView ? `Invoice #${quickView.id}` : '')}
                statusColor={statusColor(quickView?.status)}
                statusLabel={quickView?.status}
                facts={quickView ? [
                    { label: 'Project', value: quickView.projectName || '—' },
                    { label: 'Date', value: formatDate(quickView.invoiceDate) },
                    { label: 'Due date', value: formatDate(quickView.dueDate) },
                    creditedTotal(quickView) > 0
                        ? { label: 'Net amount', value: formatAmount((quickView.totalAmount || 0) - creditedTotal(quickView)) }
                        : { label: 'Amount', value: formatAmount(quickView.totalAmount) },
                ] : []}
                loadPdf={() => PortalInvoiceService.downloadPdf(quickView.id)}
                pdfErrorLabel="PDF preview not available"
                actions={quickView && (
                    <Tooltip title="Download PDF">
                        <span>
                            <IconButton
                                size="small"
                                onClick={() => handleDownload(quickView)}
                                disabled={downloadingId === quickView.id}
                                sx={{ color: '#427898', flexShrink: 0 }}
                            >
                                {downloadingId === quickView.id
                                    ? <CircularProgress size={18} sx={{ color: '#427898' }} />
                                    : <DownloadIcon fontSize="small" />}
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
                footer={quickView && (quickView.creditNotes || []).length > 0 && (
                    <Box sx={{ borderTop: '1px solid #e6ecf1', px: 3, py: 2, bgcolor: '#fffbf5' }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#e65100', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <ReceiptIcon sx={{ fontSize: 15 }} />
                            Credit Notes
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {quickView.creditNotes.map((cn, i) => (
                                <Box key={cn.id}>
                                    {i > 0 && <Divider sx={{ mb: 1 }} />}
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                                        <Box sx={{ flex: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1a2332' }}>
                                                    {cn.displayName}
                                                </Typography>
                                                <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>
                                                    {formatDate(cn.issueDate)}
                                                </Typography>
                                            </Box>
                                            {cn.reason && (
                                                <Typography sx={{ fontSize: 12, color: '#64748b' }}>
                                                    {cn.reason}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#e65100' }}>
                                                − {formatAmount(cn.amount)}
                                            </Typography>
                                            <Tooltip title="Download credit note PDF">
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDownloadCreditNote(cn)}
                                                        disabled={downloadingId === `cn-${cn.id}`}
                                                        sx={{ color: '#427898' }}
                                                    >
                                                        {downloadingId === `cn-${cn.id}`
                                                            ? <CircularProgress size={16} sx={{ color: '#427898' }} />
                                                            : <DownloadIcon sx={{ fontSize: 16 }} />}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                        <Divider sx={{ mt: 1.5, mb: 1 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 3 }}>
                            <Typography sx={{ fontSize: 12, color: '#64748b' }}>
                                Invoice total: <strong>{formatAmount(quickView.totalAmount)}</strong>
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: '#e65100' }}>
                                Credits: <strong>− {formatAmount(creditedTotal(quickView))}</strong>
                            </Typography>
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1a2332' }}>
                                Net: {formatAmount((quickView.totalAmount || 0) - creditedTotal(quickView))}
                            </Typography>
                        </Box>
                    </Box>
                )}
            />
        </PortalLayout>
    );
}
